import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { admin } from '../../api/client.js'
import { useApi } from '../../api/useApi.js'
import { pickI18n } from '../../i18n'
import { pct, timeAgo } from '../../lib.js'
import { ErrorState, Loading } from '../states.jsx'
import { AdminNav } from './AdminFeedersPage.jsx'

const LEVEL = {
  bad: 'border-live bg-live/10',
  warn: 'border-[#d99a00] bg-[#d99a00]/10',
  ok: 'border-line bg-surface',
  off: 'border-line bg-paper opacity-60',
}
const ISSUE = { bad: 'bg-live text-white', warn: 'bg-[#d99a00] text-[#1e2a35]', info: 'bg-paper text-subtle' }

/** All feeders at a glance, worst first. Refreshes every 30 s. */
export default function AdminHealthPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const data = useApi(() => admin.health())
  useEffect(() => {
    const id = setInterval(data.reload, 30000)
    return () => clearInterval(id)
  }, [data.reload])

  if (data.loading && !data.data) return <Loading />
  if (data.error) return <ErrorState />
  const h = data.data
  const v = (x, unit = '', digits = 0) => (x == null ? '—' : `${Number(x).toFixed(digits)}${unit}`)

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pt-8 pb-16 sm:px-8">
      <div className="flex flex-col gap-3">
        <h1 className="text-[clamp(28px,3.5vw,40px)] font-black tracking-[-1px]">{t('admin.health.title')}</h1>
        <AdminNav />
      </div>
      <div className="flex flex-wrap gap-3 text-sm font-bold">
        <span className="rounded-full bg-paper px-4 py-2">{t('admin.health.online', { online: h.online, total: h.total })}</span>
        <span className="rounded-full bg-live px-4 py-2 text-white">{t('admin.health.bad', { count: h.counts.bad })}</span>
        <span className="rounded-full bg-[#d99a00] px-4 py-2 text-[#1e2a35]">{t('admin.health.warn', { count: h.counts.warn })}</span>
        <span className="rounded-full bg-teal px-4 py-2 text-on-teal">{t('admin.health.ok', { count: h.counts.ok })}</span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(330px,100%),1fr))] gap-4">
        {h.feeders.map((f) => (
          <Link
            key={f.slug}
            to={`/admin/feeders/${f.slug}`}
            className={`flex flex-col gap-3 rounded-2xl border-[1.5px] p-4 text-ink no-underline ${LEVEL[f.level]}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-extrabold">
                <span className={`mr-2 inline-block size-2.5 rounded-full ${f.online ? 'bg-teal-soft' : 'bg-faint'}`} />
                {pickI18n(f.name, lang)}
              </span>
              <span className="text-xs text-subtle">{f.last_seen ? timeAgo(f.last_seen, lang) : '—'}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <span><b className="block text-lg">{pct(f.battery_percent) ?? '—'}</b>{t('admin.battery')}</span>
              <span><b className="block text-lg">{pct(f.food_level_percent) ?? '—'}</b>{t('admin.food')}</span>
              <span><b className="block text-lg">{v(f.temperature_c, '°')}</b>{t('admin.temperature')}</span>
              <span><b className="block text-lg">{v(f.solar_voltage, ' V', 1)}</b>{t('admin.solar')}</span>
              <span><b className="block text-lg">{v(f.rssi, '')}</b>{t('admin.signal')}</span>
              <span><b className="block text-lg">{f.meals_today}</b>{t('admin.health.mealsToday')}</span>
            </div>
            {f.issues.length ? (
              <div className="flex flex-wrap gap-1.5">
                {f.issues.map((i) => (
                  <span key={i.key} className={`rounded-full px-2.5 py-1 text-xs font-extrabold ${ISSUE[i.level]}`}>
                    {t(`admin.health.issues.${i.key}`, { value: i.value })}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm font-bold text-teal">✓ {t('admin.health.allGood')}</span>
            )}
          </Link>
        ))}
      </div>
    </main>
  )
}
